import { createApi, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import moment from 'moment';
import { config } from '../../config/config';
import {
  DeviceDetails,
  IMethodsInfoByDeviceId,
  NewDevice,
  RegisteredDevice,
  TelemetryDeviceGet,
  IPostSingletonStaticMethod,
  IGlobalDeviceAnnounceItem,
  ILastDeviceAnnouncePayloadResponse,
  IDeviceAnnounceByDeviceId,
  IRecordsSyncStatus,
  ISetWifiCredentialsPayload,
  IDetailsRecordDbData,
  IFileDevice,
  IScheduleFirmwareData,
  IDealerDeviceStateCount,
  IFilesSyncByDevice,
  ISystemStateData,
  IResponseRegisteredDevices,
  IConnectivityStatusByLocation,
  LightingAction,
  LightingEvent
} from '../../types/Device/DeviceInterfaces';
import { ILZTimelineDeviceData } from '../../types/deviceConnection';
import { Folder } from '../../types/folders/folder';
import customFetchBase from '../expiredToken/expiredToken';
import {
  AddLocationInOrg,
  LocationAssign,
  LocationAssignToOrganization,
  LocationByUser,
  LocationInOrganization,
  LocationNew,
  LocationSelected,
  LocationUserSelected,
  TransferLocationWithEmail
} from '../../types/location/location';
import {
  AddBusinessAccountUser,
  BusinessAccount,
  BusinessAccountUser,
  EditBusinessAccount
} from '../../types/businessAccount/businessAccount';
import {
  Organization,
  TransferOrganizationWithEmail
} from '../../types/organizations/organizations';
import { UsersByLocation } from '../../types/users';
import { PieChartStatus } from '../../constants/enums/dashboardEnums';
import { CommandType } from '../../constants/enums/commandsDecives';
import { ILZTimelineData } from '../../types/timeline/timelineInterface';
import { ResetFactoryControllers } from '../../templates/device/resetFactoryControllers/resetFactoryController';

const customFetch = customFetchBase(config.baseUrlDealers);

export const apiDevices = createApi({
  baseQuery: customFetch,
  endpoints: (builder) => ({
    getDeviceMethods: builder.mutation<IMethodsInfoByDeviceId[], string>({
      query: (deviceId) => ({
        url: `api/Device/DeviceMethodsByDeviceId?deviceId=${deviceId}`,
        method: 'GET'
      })
    }),
    addDevice: builder.mutation<{}, NewDevice>({
      query: (data) => ({
        url: 'api/RegisterDevice',
        method: 'POST',
        body: {
          deviceId: data.macAddress,
          controllerTypeId: data.productType
        }
      })
    }),
    getCredentials: builder.mutation<string[], string>({
      query: (deviceId) => ({
        url: `api/Device/GetCredentials/${deviceId}`,
        method: 'GET'
      })
    }),
    deleteDevice: builder.mutation<{}, string[]>({
      query: (deviceIds) => ({
        url: `/api/Device/RegisteredDevice?ids=${deviceIds.join(',')}`,
        method: 'DELETE'
      })
    }),
    getDevices: builder.mutation<
      IResponseRegisteredDevices,
      { page: number; size: number; additionalParams: { [key: string]: any } }
    >({
      query: (payload) => ({
        // Served by HLS; an absolute URL bypasses the HLR base URL, while token refresh stays on HLR.
        url: `${config.baseUrlSystem}api/Device/RegisteredDevices`,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        params: Object.fromEntries(
          Object.entries({
            page: payload.page,
            size: payload.size,
            ...payload.additionalParams
          }).filter(([_, value]) => value !== undefined && value !== null)
        ),
        responseHandler: async (response) => {
          const data: IResponseRegisteredDevices = await response.json();
          return {
            data: data.data.map((device: RegisteredDevice) => ({
              ...device,
              statusLabel:
                device.connectionStatus === PieChartStatus.onlineMoreThan15 ||
                device.connectionStatus === PieChartStatus.onlineLessThan15
                  ? `On since ${
                      device.lastUpdate !== '1/1/0001 12:00:00 AM'
                        ? moment
                            .utc(device.lastUpdate)
                            .local()
                            .format('YYMMDD HH:mm')
                        : '-'
                    }`
                  : `Off since ${
                      device.lastUpdate !== '1/1/0001 12:00:00 AM'
                        ? moment
                            .utc(device.lastUpdate)
                            .local()
                            .format('YYMMDD HH:mm')
                        : '-'
                    }`
            })),
            totalItems: data.totalItems,
            page: data.page
          };
        }
      })
    }),
    putDeviceName: builder.mutation<{}, { deviceId: string; name: string }>({
      query: (data) => ({
        url: `api/Device/Name/${data.deviceId}`,
        method: 'PUT',
        body: { name: data.name }
      })
    }),
    getDebugDevice: builder.mutation<[], string>({
      query: (deviceId) => ({
        url: `api/Device/Debug/${deviceId}`,
        method: 'GET'
      })
    }),
    postStaticMethod: builder.mutation<any, {}>({
      query: (data) => ({
        url: 'api/Device/DeviceStaticMethod',
        method: 'POST',
        body: data
      })
    }),
    postSingletonMethod: builder.mutation<any, IPostSingletonStaticMethod[]>({
      query: (data) => ({
        url: 'api/Device/DeviceSingletonMethod',
        method: 'POST',
        body: data
      })
    }),

    updateMethodsOrder: builder.mutation<
      any,
      { data: IPostSingletonStaticMethod[]; type: string }
    >({
      query: (data) => ({
        url: `api/Device/Device${data.type}Method`,
        method: 'PUT',
        body: data.data
      })
    }),

    deleteStaticMethod: builder.mutation<any, number>({
      query: (deviceId) => ({
        url: `api/Device/DeviceStaticMethod/${deviceId}`,
        method: 'DELETE'
      })
    }),
    deleteSingletonMethod: builder.mutation<any, number>({
      query: (deviceId) => ({
        url: `api/Device/DeviceSingletonMethod/${deviceId}`,
        method: 'DELETE'
      })
    }),
    getDeviceAnnounce: builder.mutation<IDeviceAnnounceByDeviceId[], string>({
      query: (deviceId) => ({
        url: `api/Device/DeviceAnnounce/${deviceId}`,
        method: 'GET'
      })
    }),
    getLocationsByLoggedUser: builder.mutation<LocationByUser[], {}>({
      query: () => ({
        url: `api/Location/GetLoggedUserLocations`,
        method: 'GET'
      })
    }),
    getFoldersTree: builder.mutation<Folder[], {}>({
      query: () => ({
        url: 'api/Folder/UserFolderTrees',
        method: 'GET'
      })
    }),
    getFoldersUserTree: builder.mutation<Folder[], string>({
      query: (userId) => ({
        url: `api/Folder/${userId}/UserFolderTrees`,
        method: 'GET'
      })
    }),
    postFolders: builder.mutation<
      [],
      {
        Name: string;
        ParentId: number | null;
      }
    >({
      query: (data) => ({
        url: `api/Folder`,
        method: 'POST',
        body: data
      })
    }),
    postLocation: builder.mutation<[], LocationNew>({
      query: (data) => ({
        url: `api/Location`,
        method: 'POST',
        body: data
      })
    }),
    putLocation: builder.mutation<[], LocationNew>({
      query: (data) => ({
        url: `api/Location`,
        method: 'PUT',
        body: data
      })
    }),
    assignLocationToFolder: builder.mutation<[], LocationAssign>({
      query: (data) => ({
        url: `/api/Folder/FolderLocation`,
        method: 'POST',
        body: data
      })
    }),
    getOrganizations: builder.mutation<Organization[], string>({
      query: () => ({
        url: `/api/Organization/OrganizationsSummary`,
        method: 'GET'
      })
    }),
    postOrganization: builder.mutation<
      number,
      {
        Name: string;
        ParentId: number | null;
        OwnerId: number | null;
      }
    >({
      query: (data) => ({
        url: `api/Organization`,
        method: 'POST',
        body: data
      })
    }),
    deleteOrganization: builder.mutation<any, number[]>({
      query: (organizationIds) => ({
        url: `/api/Organization?organizationIds=${organizationIds.join(',')}`,
        method: 'DELETE'
      })
    }),
    assignLocationToOrganization: builder.mutation<
      any,
      LocationAssignToOrganization
    >({
      query: (data) => ({
        url: `/api/Organization/OrganizationLocation`,
        method: 'POST',
        body: data
      })
    }),
    deleteLocationInOrganization: builder.mutation<any, number>({
      query: (organizationId) => ({
        url: `/api/Organization/OrganizationLocation?organizationLocationId=${organizationId}`,
        method: 'DELETE'
      })
    }),
    getOrganizationLocations: builder.mutation<
      LocationInOrganization[],
      string
    >({
      query: (organizationId) => ({
        url: `/api/Organization/OrganizationLocations/${organizationId}`,
        method: 'GET'
      })
    }),
    transferOrg: builder.mutation<{}, TransferOrganizationWithEmail>({
      query: (data) => ({
        url: `/api/Organization/TransferOrganizationUsingEmail`,
        method: 'POST',
        body: data
      })
    }),
    transferLoc: builder.mutation<{}, TransferLocationWithEmail>({
      query: (data) => ({
        url: `/api/Location/TransferLocationUsingEmail`,
        method: 'POST',
        body: data
      })
    }),
    ShareOrg: builder.mutation<
      {},
      {
        UserId: number;
        OrganizationRoleId: number;
        OrganizationId: number;
      }
    >({
      query: (data) => ({
        url: `api/Organization/ShareOrganization`,
        method: 'POST',
        body: data
      })
    }),
    ShareLoc: builder.mutation<
      {},
      {
        userId: number;
        locationRoleId: number;
        locationId: number;
      }
    >({
      query: (data) => ({
        url: `api/Location/ShareLocation`,
        method: 'POST',
        body: data
      })
    }),
    getRolesLocation: builder.mutation<{ id: number; name: string }[], string>({
      query: () => ({
        url: `api/LocationPermission/Roles`,
        method: 'GET'
      })
    }),
    getRolesOrganization: builder.mutation<
      { id: number; name: string }[],
      string
    >({
      query: () => ({
        url: `api/OrganizationPermission/Roles`,
        method: 'GET'
      })
    }),
    getLocationsInOrganization: builder.mutation<
      { locations: LocationInOrganization[]; organizations: [] },
      string
    >({
      query: (organizationId) => ({
        url: `/api/Organization/LocationsAndOrganizations/${organizationId}`,
        method: 'GET'
      })
    }),
    getAllBusinessAccount: builder.mutation<BusinessAccount[], string>({
      query: () => ({
        url: `/api/BusinessAccount/AllBusinessAccounts`,
        method: 'GET'
      })
    }),
    getBusinessAccountByUser: builder.mutation<BusinessAccount[], string>({
      query: (idOwner) => ({
        url: `/api/BusinessAccount/${idOwner}`,
        method: 'GET'
      })
    }),
    getBusinessAccountUsers: builder.mutation<BusinessAccountUser[], string>({
      query: (idBusinessAccount) => ({
        url: `/api/BusinessAccountUser/${idBusinessAccount}`,
        method: 'GET'
      })
    }),
    addBusinessAccountUser: builder.mutation<{}, AddBusinessAccountUser>({
      query: (data) => ({
        url: `api/BusinessAccountUser`,
        method: 'POST',
        body: data
      })
    }),
    editBusinessAccountUser: builder.mutation<{}, AddBusinessAccountUser>({
      query: (data) => ({
        url: `api/BusinessAccountUser`,
        method: 'PUT',
        body: data
      })
    }),
    deleteBusinessAccountUser: builder.mutation<{}, string>({
      query: (userId) => ({
        url: `api/BusinessAccountUser/${userId}`,
        method: 'DELETE'
      })
    }),
    editBusinessAccount: builder.mutation<{}, EditBusinessAccount>({
      query: (data) => ({
        url: `/api/BusinessAccount`,
        method: 'PUT',
        body: data
      })
    }),
    deleteBusinessAccount: builder.mutation<{}, string>({
      query: (businessId) => ({
        url: `api/BusinessAccount/${businessId}`,
        method: 'DELETE'
      })
    }),
    userSelectedLocation: builder.mutation<{}, LocationSelected>({
      query: (data) => ({
        url: `/api/Location/UserSelectedLocation`,
        method: 'POST',
        body: data
      })
    }),
    getUserSelectedLocation: builder.mutation<
      LocationUserSelected[],
      { userId: string; searchText: string }
    >({
      query: (body) => ({
        url: `/api/Location/UserLocations/${body.userId}?filterByName=${body.searchText}`,
        method: 'GET'
      })
    }),
    getAllProductNames: builder.mutation<
      { id: number; name: string }[],
      string
    >({
      query: () => ({
        url: `/api/Product/AllProductNames`,
        method: 'GET'
      })
    }),
    shareOrganizationUsingEmail: builder.mutation<
      {},
      {
        email: string;
        organizationIds: number[];
        organizationRoleId: number;
      }
    >({
      query: (data) => ({
        url: `/api/Organization/ShareOrganizationUsingEmail`,
        method: 'POST',
        body: data
      })
    }),
    shareLocationWithBusinessAccount: builder.mutation<
      {},
      {
        businessAccountTag: string;
        locationRoleId: number;
        locationIds: number[];
      }
    >({
      query: (data) => ({
        url: `/api/Location/ShareLocationWithBusinessAccountUsingTag`,
        method: 'POST',
        body: data
      })
    }),
    shareOrganizationWithBusinessAccount: builder.mutation<
      {},
      {
        businessAccountTag: string;
        organizationRoleId: number;
        organizationIds: number[];
      }
    >({
      query: (data) => ({
        url: `/api/Organization/ShareOrganizationWithBusinessAccountUsingTag`,
        method: 'POST',
        body: data
      })
    }),
    addLocationInOrg: builder.mutation<{}, AddLocationInOrg>({
      query: (data) => ({
        url: `/api/Organization/OrganizationLocation`,
        method: 'POST',
        body: data
      })
    }),
    deleteUserFromLocation: builder.mutation<{}, {}>({
      query: (userLocationId: number) => ({
        url: `api/Location/LocationUser/${userLocationId}`,
        method: 'DELETE'
      })
    }),
    getLocationsUsersByLocationId: builder.mutation<UsersByLocation[], {}>({
      query: (locationId: number) => ({
        url: `api/Location/GetLocationUsersByLocationId/${locationId}`,
        method: 'GET'
      })
    }),
    getRecordsByDb: builder.mutation<IDetailsRecordDbData, TelemetryDeviceGet>({
      query: (data) => ({
        url: `api/Device/Records?deviceId=${data.deviceId}&dbName=${data.dbName}&Page=${data.page}&Size=${data.pageSize}`,
        method: 'GET'
      })
    }),
    getRecordsSyncStatus: builder.mutation<IRecordsSyncStatus[], string>({
      query: (deviceId) => ({
        url: `api/Device/Records/SyncStatus/${deviceId}`,
        method: 'GET'
      })
    }),
    getRecordsDbNames: builder.mutation<string[], string>({
      query: (deviceId) => ({
        url: `api/Device/Records/${deviceId}/DbNames`,
        method: 'GET'
      })
    }),
    putRegenerateCredentials: builder.mutation<{}, string>({
      query: (deviceId) => ({
        url: `api/Device/RegenerateCredentials`,
        body: {
          deviceId
        },
        method: 'PUT'
      })
    }),
    getDeviceInfo: builder.mutation<DeviceDetails, string>({
      query: (deviceId) => ({
        url: `api/Device/Details/${deviceId}`,
        method: 'GET'
      })
    }),
    deleteMultipleDeviceMethods: builder.mutation<
      {},
      { singletonIds: number[]; staticIds: number[] }
    >({
      query: (data) => ({
        url: `api/Device/DeviceMethod?singletonIds=${data.singletonIds.join(
          ','
        )}&staticIds=${data.staticIds.join(',')}`,
        method: 'DELETE'
      })
    }),
    postCustomFileDevice: builder.mutation<
      {},
      { deviceId: string; data: FormData }
    >({
      query: (data) => ({
        url: `api/TestMessage/CustomFIle/${data.deviceId}`,
        method: 'POST',
        body: data.data
      })
    }),
    deleteDbFromDevice: builder.mutation<
      {},
      { deviceId: string; dbName: string }
    >({
      query: (data) => ({
        url: `api/Device/Records?DeviceId=${data.deviceId}&DBName=${data.dbName}`,
        method: 'DELETE'
      })
    }),
    getGlobalDeviceAnnounce: builder.mutation<
      IGlobalDeviceAnnounceItem[],
      void
    >({
      query: () => ({
        url: `api/Device/GlobalDeviceAnnounceReport`,
        method: 'GET'
      })
    }),
    getLastDeviceAnnounceByDeviceId: builder.mutation<
      ILastDeviceAnnouncePayloadResponse,
      string
    >({
      query: (deviceId) => ({
        url: `api/Device/LastDeviceAnnounce/${deviceId}`,
        method: 'GET'
      })
    }),
    getFilesSyncByDeviceId: builder.mutation<any, string>({
      query: (deviceId) => ({
        url: `/api/FilesSync/DesiredProperties/${deviceId}`,
        method: 'GET'
      })
    }),
    toggleSyncStatusRecordDb: builder.mutation<
      {},
      { deviceId: string; dbName: string; toggleText: string }
    >({
      query: (data) => ({
        url: `api/Device/Records/SyncStatus/${data.toggleText}`,
        method: 'POST',
        body: {
          deviceId: data.deviceId,
          dbName: data.dbName
        }
      })
    }),
    getFileSyncDataRetriever: builder.mutation<
      { columns: { name: string; dataType: string }[]; rows: [] }[],
      string
    >({
      query: (url) => ({
        url: `/api/FilesSync/DataRetriever`,
        method: 'POST',
        body: { url }
      })
    }),
    deviceRebootCommand: builder.mutation<{}, string[]>({
      query: (deviceIds) => ({
        url: `api/Commands/Reboot`,
        method: 'POST',
        body: { deviceIds }
      })
    }),
    deviceSetWifiCredentials: builder.mutation<{}, ISetWifiCredentialsPayload>({
      query: (data) => ({
        url: `api/Commands/SetWifiCredentials`,
        method: 'POST',
        body: data
      })
    }),
    getListOfFilesCommandByDeviceId: builder.mutation<IFileDevice[], string>({
      query: (deviceId) => ({
        url: `api/Commands/FileList/${deviceId}`,
        method: 'GET'
      })
    }),
    sendFileThroughEmail: builder.mutation<
      {},
      { deviceId: string; fileNames: string[] }
    >({
      query: (data) => ({
        url: `api/Commands/SendFilesThroughEmail`,
        method: 'POST',
        body: data
      })
    }),
    scheduleFirmwareUpdate: builder.mutation<
      {},
      { versionId: number; deviceIds: string[]; upgradeTypeId: number }
    >({
      query: (data) => ({
        url: `api/Firmware/Schedule`,
        method: 'POST',
        body: data
      })
    }),

    getFirmwareVersions: builder.mutation<
      {
        data: {
          id: number;
          name: string;
          minWifiModuleVersionName: string;
          active: boolean;
        }[];
      },
      number[]
    >({
      query: (productTypeIds) => ({
        url: `api/Firmware/Version?supportedModels=${productTypeIds.join(
          ','
        )}&active=true`,
        method: 'GET'
      })
    }),
    getSchedulesFirmwareUpdate: builder.mutation<
      { data: IScheduleFirmwareData[] },
      void
    >({
      query: () => ({
        url: `api/Firmware/Schedule`,
        method: 'GET'
      })
    }),
    getDealerDeviceStateCount: builder.mutation<
      IDealerDeviceStateCount[],
      void
    >({
      query: () => ({
        url: `api/Device/DealerDevicesStateCount`,
        method: 'GET'
      })
    }),
    deleteDeviceCredentials: builder.mutation<{}, string>({
      query: (deviceId) => ({
        url: `api/Commands/DeleteCredentials`,
        method: 'POST',
        body: {
          deviceId
        }
      })
    }),
    fileSyncDesiredProperties: builder.mutation<IFilesSyncByDevice[], string>({
      query: (deviceId) => ({
        url: `/api/FilesSync/DesiredProperties/${deviceId}`,
        method: 'GET'
      })
    }),
    sendDirectMethod: builder.mutation<
      {},
      { deviceIds: string[]; plainStringCommand: string }
    >({
      query: (data) => ({
        url: `api/Commands/PlainJsonString`,
        method: 'POST',
        body: data
      })
    }),
    refreshStatusDevices: builder.mutation<{}, number>({
      query: (locationId) => ({
        url: `api/Device/ConnectionState/${locationId}`,
        method: 'PUT'
      })
    }),
    getSystemState: builder.mutation<ISystemStateData, string>({
      query: (deviceId) => ({
        url: `api/Commands/SystemState/${deviceId}`,
        method: 'GET'
      }),
      transformErrorResponse: (baseQueryReturnValue: FetchBaseQueryError) => {
        return {
          success: false,
          returnCode: baseQueryReturnValue.status,
          message: (baseQueryReturnValue.data as { message?: string })?.message
        };
      }
    }),
    getTwinInfo: builder.mutation<{}, string>({
      query: (deviceId) => ({
        url: `api/Device/TwinInfo/${deviceId}`,
        method: 'GET'
      })
    }),
    getDeferOnOffMethod: builder.mutation<
      {},
      { locationId: number; method: string; date: string }
    >({
      query: (data) => ({
        url: `api/Devices/${data.method}`,
        method: 'POST',
        body: {
          locationId: data.locationId,
          date: data.date
        }
      })
    }),
    sendProFlag: builder.mutation<{}, { deviceIds: string[]; isPro: boolean }>({
      query: (data) => ({
        url: `api/Commands/SetProFlag`,
        method: 'POST',
        body: data
      })
    }),
    regenerateIotHubCredentials: builder.mutation<{}, { deviceId: string }>({
      query: (deviceId) => ({
        url: `api/Device/RegenerateIotHubCredentials`,
        method: 'PUT',
        body: deviceId
      })
    }),
    connectivityStatusByLocation: builder.mutation<
      IConnectivityStatusByLocation[],
      number
    >({
      query: (locationId) => ({
        url: `api/Device/ConnectivityStatusByLocation/${locationId}`,
        method: 'GET'
      })
    }),
    getTimelineByDeviceId: builder.mutation<
      ILZTimelineData,
      { deviceId: string; isZone: boolean; lightZoneId: number }
    >({
      query: (data) => ({
        url: `api/Device/Timeline/${data.deviceId}`,
        method: 'GET',
        params: {
          isZone: data.isZone,
          lightZoneId: data.lightZoneId
        }
      })
    }),
    getLZByDeviceId: builder.mutation<
      { id: number; name: string; isZone: boolean }[],
      string
    >({
      query: (deviceId) => ({
        url: `api/Device/${deviceId}/LightZones`,
        method: 'GET'
      })
    }),
    sendMultipleFiles: builder.mutation<
      {},
      { deviceIds: string[]; fileNames: string[] }
    >({
      query: (data) => ({
        url: `api/Commands/SendMultipleFilesThroughEmail`,
        method: 'POST',
        body: data
      })
    }),
    getDeviceD2c: builder.mutation<any, string>({
      query: (deviceId) => ({
        url: `api/Device/LastStatusMessage/${deviceId}`,
        method: 'GET'
      })
    }),
    getBrand: builder.mutation<any, any>({
      query: () => ({
        url: `api/Brands`,
        method: 'GET'
      })
    }),
    getDeviceTimeline: builder.mutation<ILZTimelineDeviceData, string>({
      query: (deviceId) => ({
        url: `api/Device/Timeline/ConnectionState/${deviceId}`,
        method: 'GET'
      })
    }),
    getDeviceActionTable: builder.mutation<LightingAction[], string>({
      query: (deviceId) => ({
        url: `api/Commands/GetActionTable/${deviceId}`,
        method: 'GET'
      })
    }),
    getDeviceEventTable: builder.mutation<LightingEvent[], string>({
      query: (deviceId) => ({
        url: `api/Commands/GetEventTable/${deviceId}`,
        method: 'GET'
      })
    }),
    sendMSendSetting: builder.mutation<
      {},
      { deviceIds: string[]; mSendValue: number }
    >({
      query: (data) => ({
        url: `api/Commands/MSendSetting`,
        method: 'POST',
        body: data
      })
    }),
    sendEventRepeatSetting: builder.mutation<
      {},
      { deviceIds: string[]; eventRepeatValue: number }
    >({
      query: (data) => ({
        url: `api/Commands/MSendSetting`,
        method: 'POST',
        body: data
      })
    }),
    sendCommandRepeatSetting: builder.mutation<
      {},
      { deviceIds: string[]; commandRepeatValue: number }
    >({
      query: (data) => ({
        url: `api/Commands/CommandRepeatSetting`,
        method: 'POST',
        body: data
      })
    }),
    sendCommandBitfield: builder.mutation<
      {},
      { deviceIds: string[]; bitFieldEnable: boolean }
    >({
      query: (data) => ({
        url: `api/Commands/BitField`,
        method: 'POST',
        body: data
      })
    }),
    getDeferEvent: builder.mutation<any, { deviceIds: string[] }>({
      query: (data) => ({
        url: `api/Devices/GetUtcTime`,
        method: 'POST',
        body: data.deviceIds
      })
    }),
    getDeferEventOn: builder.mutation<any, { deviceIds: string[] }>({
      query: (data) => ({
        url: `api/Devices/GetDeferOnTime`,
        method: 'POST',
        body: data.deviceIds
      })
    }),
    getDeferEventOff: builder.mutation<any, { deviceIds: string[] }>({
      query: (data) => ({
        url: `api/Devices/GetDeferOffTime`,
        method: 'POST',
        body: data.deviceIds
      })
    }),
    setDeferEventOn: builder.mutation<
      any,
      { locationId: number; date: string }
    >({
      query: (data) => ({
        url: `api/Devices/DeferOnCommand`,
        method: 'POST',
        body: data
      })
    }),
    setDeferEventOff: builder.mutation<
      any,
      { locationId: number; date: string }
    >({
      query: (data) => ({
        url: `api/Devices/DeferOffCommand`,
        method: 'POST',
        body: data
      })
    }),
    executeMultipleProdCommands: builder.mutation<
      any,
      {
        commands: {
          CommandType: CommandType;
          NtpInterval?: number;
          BitFieldEnable?: boolean;
          LightNtpInterval?: number;
          MSendValue?: number;
          CommandRepeat?: number;
          EventRepeat?: number;
          StartTime?: string;
          StopTime?: string;
          StatusInterval?: number;
          CommunicationDelay?: number;
          MacAddresses: string[];
        }[];
      }
    >({
      query: ({ commands }) => ({
        url: `api/Commands/ExecuteMultipleProd`,
        method: 'POST',
        body: commands
      })
    }),
    testDevice: builder.mutation<
      {
        requestDurations: string[];
      },
      {
        DeviceId: string;
        MethodName: string;
        Payload: string;
        TotalRequests: number;
        WaitForResponse: boolean;
        Delay: number;
      }
    >({
      query: (data) => ({
        url: `api/Commands/MassiveTest`,
        method: 'POST',
        body: data
      })
    }),
    getFirmwareVersion: builder.mutation<string[], ''>({
      query: () => ({
        url: `api/Firmware/Version/List`,
        method: 'GET'
      })
    }),
    ResetFactoryControllerBadKey: builder.mutation<any, { deviceId: string }>({
      query: (data) => ({
        url: `api/Device/ResetFactory`,
        method: 'POST',
        body: { deviceId: data.deviceId }
      })
    })
  })
});

export const {
  useAddDeviceMutation,
  useGetDevicesMutation,
  useDeleteDeviceMutation,
  useGetDeviceMethodsMutation,
  useGetDebugDeviceMutation,
  usePostSingletonMethodMutation,
  usePostStaticMethodMutation,
  useDeleteSingletonMethodMutation,
  useDeleteStaticMethodMutation,
  useGetDeviceAnnounceMutation,
  useGetCredentialsMutation,
  useGetLocationsByLoggedUserMutation,
  useGetFoldersTreeMutation,
  useGetFoldersUserTreeMutation,
  usePostFoldersMutation,
  usePostLocationMutation,
  useAssignLocationToFolderMutation,
  useGetOrganizationsMutation,
  usePostOrganizationMutation,
  useDeleteOrganizationMutation,
  useAssignLocationToOrganizationMutation,
  useDeleteLocationInOrganizationMutation,
  useGetOrganizationLocationsMutation,
  useTransferOrgMutation,
  useTransferLocMutation,
  useShareOrgMutation,
  useShareLocMutation,
  useGetRolesLocationMutation,
  useGetRolesOrganizationMutation,
  useGetLocationsInOrganizationMutation,
  useGetAllBusinessAccountMutation,
  useGetBusinessAccountByUserMutation,
  useGetBusinessAccountUsersMutation,
  useAddBusinessAccountUserMutation,
  useEditBusinessAccountUserMutation,
  useDeleteBusinessAccountUserMutation,
  useDeleteBusinessAccountMutation,
  useUserSelectedLocationMutation,
  useGetUserSelectedLocationMutation,
  useGetAllProductNamesMutation,
  useShareOrganizationUsingEmailMutation,
  useShareLocationWithBusinessAccountMutation,
  useShareOrganizationWithBusinessAccountMutation,
  useEditBusinessAccountMutation,
  useAddLocationInOrgMutation,
  useDeleteUserFromLocationMutation,
  useGetLocationsUsersByLocationIdMutation,
  useGetRecordsByDbMutation,
  usePutLocationMutation,
  useGetRecordsDbNamesMutation,
  usePutRegenerateCredentialsMutation,
  useGetDeviceInfoMutation,
  useDeleteMultipleDeviceMethodsMutation,
  usePostCustomFileDeviceMutation,
  useDeleteDbFromDeviceMutation,
  useGetGlobalDeviceAnnounceMutation,
  useGetLastDeviceAnnounceByDeviceIdMutation,
  useUpdateMethodsOrderMutation,
  useGetRecordsSyncStatusMutation,
  useToggleSyncStatusRecordDbMutation,
  useGetFilesSyncByDeviceIdMutation,
  useGetFileSyncDataRetrieverMutation,
  useDeviceRebootCommandMutation,
  useDeviceSetWifiCredentialsMutation,
  useGetListOfFilesCommandByDeviceIdMutation,
  useSendFileThroughEmailMutation,
  useScheduleFirmwareUpdateMutation,
  useGetFirmwareVersionsMutation,
  useGetSchedulesFirmwareUpdateMutation,
  useGetDealerDeviceStateCountMutation,
  useDeleteDeviceCredentialsMutation,
  useFileSyncDesiredPropertiesMutation,
  useSendDirectMethodMutation,
  useRefreshStatusDevicesMutation,
  useGetSystemStateMutation,
  useGetTwinInfoMutation,
  usePutDeviceNameMutation,
  useGetDeferOnOffMethodMutation,
  useSendProFlagMutation,
  useRegenerateIotHubCredentialsMutation,
  useConnectivityStatusByLocationMutation,
  useGetTimelineByDeviceIdMutation,
  useGetLZByDeviceIdMutation,
  useSendMultipleFilesMutation,
  useGetDeviceD2cMutation,
  useGetBrandMutation,
  useGetDeviceTimelineMutation,
  useGetDeviceActionTableMutation,
  useGetDeviceEventTableMutation,
  useSendMSendSettingMutation,
  useSendEventRepeatSettingMutation,
  useSendCommandRepeatSettingMutation,
  useSendCommandBitfieldMutation,
  useGetDeferEventMutation,
  useGetDeferEventOnMutation,
  useGetDeferEventOffMutation,
  useSetDeferEventOffMutation,
  useSetDeferEventOnMutation,
  useExecuteMultipleProdCommandsMutation,
  useTestDeviceMutation,
  useGetFirmwareVersionMutation,
  useResetFactoryControllerBadKeyMutation
} = apiDevices;
